// Post-match DOM overlay. Shows final score, offers a Save-replay button,
// and a Back-to-menu button. Mirrors MainMenu's styling.
//
// If `autoDownloadOnShow` is true, the panel triggers the JSON download
// automatically as soon as `show()` is called — that's the CPU-vs-CPU
// iteration workflow ("every match writes a file I can inspect later").

import type { Replay } from "@arcade-hockey/sim";
import { serializeReplay } from "@arcade-hockey/sim";

export type ResultsPanelView = {
  homeScore: number;
  awayScore: number;
  winner: "home" | "away" | "Draw";
  replay: Replay;
  filenamePrefix: string; // e.g. "cpu-vs-cpu" or "match"
  autoDownloadOnShow: boolean;
};

export type ResultsPanelHandlers = {
  onBackToMenu: () => void;
  onPlayAgain: () => void;
};

export class ResultsPanel {
  private root: HTMLDivElement;
  private title: HTMLDivElement;
  private scoreEl: HTMLDivElement;
  private saveButton: HTMLButtonElement;
  private status: HTMLDivElement;
  private currentView: ResultsPanelView | null = null;
  private handlers: ResultsPanelHandlers;

  constructor(handlers: ResultsPanelHandlers) {
    this.handlers = handlers;

    this.root = document.createElement("div");
    Object.assign(this.root.style, OVERLAY_STYLE);

    this.title = document.createElement("div");
    Object.assign(this.title.style, HEADING_STYLE);

    this.scoreEl = document.createElement("div");
    Object.assign(this.scoreEl.style, SCORE_STYLE);

    this.saveButton = makeButton("Save replay JSON", () => this.onSave());
    Object.assign(this.saveButton.style, BUTTON_STYLE);

    const playAgainButton = makeButton("Play again (same seed)", () => this.handlers.onPlayAgain());
    Object.assign(playAgainButton.style, BUTTON_STYLE);

    const backButton = makeButton("Back to menu", () => this.handlers.onBackToMenu());
    Object.assign(backButton.style, BUTTON_STYLE);

    const buttonRow = document.createElement("div");
    Object.assign(buttonRow.style, ROW_STYLE);
    buttonRow.append(playAgainButton, backButton);

    this.status = document.createElement("div");
    Object.assign(this.status.style, STATUS_STYLE);

    this.root.append(this.title, this.scoreEl, this.saveButton, buttonRow, this.status);

    this.hide();
  }

  attach(parent: HTMLElement): void {
    parent.append(this.root);
  }

  detach(): void {
    this.root.remove();
  }

  show(view: ResultsPanelView): void {
    this.currentView = view;
    this.title.textContent =
      view.winner === "Draw" ? "Draw" : `${view.winner === "home" ? "Home" : "Away"} wins`;
    this.scoreEl.textContent = `${view.homeScore} – ${view.awayScore}`;
    this.status.textContent = "";
    this.root.style.display = "flex";
    if (view.autoDownloadOnShow) this.downloadCurrent();
  }

  hide(): void {
    this.root.style.display = "none";
  }

  private onSave(): void {
    this.downloadCurrent();
  }

  private downloadCurrent(): void {
    const view = this.currentView;
    if (view === null) return;
    const json = serializeReplay(view.replay);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${view.filenamePrefix}_seed-${view.replay.seed}_${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.status.textContent = `Saved ${a.download}`;
  }
}

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

const OVERLAY_STYLE: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: "9000",
  minWidth: "320px",
  display: "none",
  flexDirection: "column",
  gap: "12px",
  padding: "24px",
  background: "rgba(14, 17, 22, 0.96)",
  color: "#e6edf3",
  font: "13px ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid #2d3748",
  borderRadius: "8px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
  textAlign: "center",
};

const HEADING_STYLE: Partial<CSSStyleDeclaration> = {
  fontSize: "20px",
  fontWeight: "700",
  letterSpacing: "0.04em",
};

const SCORE_STYLE: Partial<CSSStyleDeclaration> = {
  fontSize: "36px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  color: "#fbbf24",
  margin: "8px 0",
};

const ROW_STYLE: Partial<CSSStyleDeclaration> = {
  display: "flex",
  gap: "8px",
  justifyContent: "center",
};

const BUTTON_STYLE: Partial<CSSStyleDeclaration> = {
  background: "#2d3748",
  color: "#e6edf3",
  border: "1px solid #4a5568",
  borderRadius: "4px",
  padding: "8px 14px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "13px",
};

const STATUS_STYLE: Partial<CSSStyleDeclaration> = {
  color: "#9ca3af",
  fontSize: "11px",
  minHeight: "14px",
};
